import { Dispatch } from 'redux';
import { WorkloadTask } from 'shovel-lib/types';

import { shovelAiApi } from '@api/shovelAiApi';
import { alert, NotificationMode } from '@components/common/snackbarNotifications/Notifications';
import { CalendarCourse } from '@state/calendar/types';
import { taskActions } from '@state/task';
import { workloadActions } from '@state/workload';
import { AI_TASK_CATEGORIZATION_DEVELOPER_MESSAGE } from '@utils/taskCategorization';

const MAX_IN_FLIGHT = 3;

export const isTaskUncategorized = (task: WorkloadTask) => !task.deleted && !!task.courseId && !task.taskCategory;

export const getUncategorizedTasksForCourse = (tasks: WorkloadTask[], courseId: number) =>
  tasks.filter(task => task.courseId === courseId && isTaskUncategorized(task));

/**
 * Runs AI categorization for a single course's currently-uncategorized tasks and persists the
 * result. Shared by the auto-on-sync heuristic and every manual "Auto-categorize" entry point
 * (task list header, Pile row, Task Estimator) so they dedupe through the same in-flight flag.
 */
export const categorizeCourse = async (
  dispatch: Dispatch,
  course: CalendarCourse,
  tasks: WorkloadTask[]
): Promise<boolean> => {
  const tasksForCourse = getUncategorizedTasksForCourse(tasks, course.id);
  if (tasksForCourse.length === 0) return true;

  dispatch(workloadActions.setCourseCategorizing({ courseId: course.id, categorizing: true }));

  try {
    const response = await shovelAiApi.categorizeTasks({
      existingCategories: (course.taskCategories || []).map(category => ({
        id: category.id,
        name: category.name,
        emoji: category.emoji
      })),
      tasks: tasksForCourse.map(task => ({
        id: task.id,
        title: task.title,
        subtitle: task.details || ''
      })),
      developerMessage: AI_TASK_CATEGORIZATION_DEVELOPER_MESSAGE
    });

    const data = response.data;
    if (!response.ok || !data) {
      alert('Failed to categorize tasks with AI.', NotificationMode.ERROR);
      return false;
    }

    const taskCount = data.taskCategoryMappings.length;
    const newCount = data.newCategories.length;
    const successMessage =
      newCount > 0
        ? `${taskCount} task${taskCount === 1 ? '' : 's'} categorized into ${newCount} new categor${
            newCount === 1 ? 'y' : 'ies'
          }`
        : `${taskCount} task${taskCount === 1 ? '' : 's'} categorized`;

    return await new Promise<boolean>(resolve => {
      dispatch(
        taskActions.tasksCategorization.request({
          courseId: course.id,
          newCategories: data.newCategories,
          taskCategoryMappings: data.taskCategoryMappings,
          silent: false,
          successMessage,
          onFinish: success => resolve(!!success)
        })
      );
    });
  } catch {
    alert('Failed to categorize tasks with AI.', NotificationMode.ERROR);
    return false;
  } finally {
    dispatch(workloadActions.setCourseCategorizing({ courseId: course.id, categorizing: false }));
  }
};

/** Runs categorizeCourse across several courses at once, throttled to MAX_IN_FLIGHT concurrent AI calls. */
export const categorizeCourses = (dispatch: Dispatch, courses: CalendarCourse[], tasks: WorkloadTask[]) => {
  const queue = [...courses];
  let inFlight = 0;

  const startNext = () => {
    while (inFlight < MAX_IN_FLIGHT && queue.length > 0) {
      const course = queue.shift();
      if (!course) break;
      inFlight += 1;
      void categorizeCourse(dispatch, course, tasks).finally(() => {
        inFlight -= 1;
        startNext();
      });
    }
  };

  startNext();
};
